export const okdocActionLabels: Record<string, string> = {
  confirm: 'Приду',
  reschedule: 'Перенести',
  cancel: 'Отменить',
};

export interface OkdocBrandTheme {
  badge: string;
  accent: string;
  soft: string;
  softPanel: string;
  header: string;
  primaryButton: string;
  secondaryButton: string;
}

export interface OkdocPatientPortalAction {
  label: string;
  url: string;
}

export interface OkdocPatientPortalConfig {
  title: string;
  description: string;
  buttonLabel: string;
  url: string;
  extraActions?: OkdocPatientPortalAction[];
}

export interface OkdocFeedbackResultCopy {
  title: string;
  description: string;
}

export const okdocBrandThemes: Record<string, OkdocBrandTheme> = {
  // 13.03.26: DHC увели в более бирюзовую спокойную палитру, ближе к референсу plans.dhcentre.
  dhc: {
    badge: 'bg-cyan-700',
    accent: 'text-cyan-800',
    soft: 'from-slate-50 via-cyan-50 to-teal-50',
    softPanel: 'from-cyan-700/12 via-teal-500/8 to-white',
    header: 'bg-gradient-to-r from-cyan-700 via-teal-700 to-cyan-800',
    primaryButton: 'bg-cyan-700 text-white hover:bg-cyan-800 focus:ring-cyan-600',
    secondaryButton: 'bg-cyan-50 text-cyan-900 hover:bg-cyan-100 focus:ring-cyan-300',
  },
  // 13.03.26: UlybkaPRO сделали в сдержанных тёмно-серых тонах вместо ярко-синих.
  ulybkapro: {
    badge: 'bg-slate-800',
    accent: 'text-slate-700',
    soft: 'from-slate-100 via-zinc-50 to-slate-200',
    softPanel: 'from-slate-900/10 via-slate-700/5 to-white',
    header: 'bg-gradient-to-r from-slate-800 via-zinc-800 to-slate-900',
    primaryButton: 'bg-slate-800 text-white hover:bg-slate-900 focus:ring-slate-700',
    secondaryButton: 'bg-slate-100 text-slate-900 hover:bg-slate-200 focus:ring-slate-300',
  },
  dentaclassic: {
    badge: 'bg-violet-600',
    accent: 'text-violet-700',
    soft: 'from-violet-50 via-white to-fuchsia-50',
    softPanel: 'from-violet-500/10 via-fuchsia-500/5 to-white',
    header: 'bg-gradient-to-r from-violet-700 via-fuchsia-700 to-violet-800',
    primaryButton: 'bg-violet-600 text-white hover:bg-violet-700 focus:ring-violet-500',
    secondaryButton: 'bg-violet-50 text-violet-800 hover:bg-violet-100 focus:ring-violet-400',
  },
  // 13.08.26: цвета взяты из implant-beauty.ru globals.css (--primary/--accent, oklch(0.52 0.16 250) / oklch(0.72 0.12 225)).
  'implant-beauty': {
    badge: 'bg-[#006ac0]',
    accent: 'text-[#006ac0]',
    soft: 'from-[#e8f3ff] via-white to-[#e8f3ff]',
    softPanel: 'from-[#006ac0]/12 via-[#39b4dd]/8 to-white',
    header: 'bg-gradient-to-r from-[#006ac0] via-[#1f8fd6] to-[#39b4dd]',
    primaryButton: 'bg-[#006ac0] text-white hover:bg-[#00579e] focus:ring-[#39b4dd]',
    secondaryButton: 'bg-[#e8f3ff] text-[#0b3a66] hover:bg-[#d6ebff] focus:ring-[#39b4dd]',
  },
};

export const okdocDefaultTheme = {
  badge: 'bg-sky-600',
  accent: 'text-sky-700',
  soft: 'from-sky-50 via-white to-sky-100',
  softPanel: 'from-sky-500/10 via-white to-sky-50',
  header: 'bg-gradient-to-r from-sky-700 via-cyan-700 to-sky-800',
  primaryButton: 'bg-sky-600 text-white hover:bg-sky-700 focus:ring-sky-500',
  secondaryButton: 'bg-sky-50 text-sky-800 hover:bg-sky-100 focus:ring-sky-400',
};

export const patientAppStores = [
  {
    id: 'app_store',
    title: 'Загрузить в App Store',
    url: 'https://apps.apple.com/ru/app/id6801889805',
    image: '/store/app-store.svg',
    width: 120,
    height: 40,
  },
  {
    id: 'google_play',
    title: 'Доступно в Google Play',
    url: 'https://play.google.com/store/apps/details?id=ru.stomatologii.medintegra.patient',
    image: '/store/google-play.png',
    width: 104,
    height: 40,
  },
] as const;

export function isPatientAppStoreTarget(id?: string, url?: string) {
  const target = (id || '').trim();
  const link = (url || '').trim();
  return (
    target === 'app_store' ||
    target === 'google_play' ||
    link.includes('apps.apple.com') ||
    link.includes('play.google.com/store/apps/details')
  );
}

export const okdocPatientPortals: Record<string, OkdocPatientPortalConfig> = {
  // 13.03.26: tenant-specific CTA для масштабируемых SMS/okdoc лендингов.
  // 24.09.26: на странице сбора ОС — запись и личный кабинет своими ссылками клиники.
  dhc: {
    title: 'Запись и личный кабинет',
    description: 'Запишитесь на приём или откройте личный кабинет: записи, приёмы и предложения.',
    buttonLabel: 'Записаться онлайн',
    url: 'https://app.dhcentre.ru',
    extraActions: [
      {
        label: 'Личный кабинет',
        url: 'https://my.dhcentre.ru/lk_patient',
      },
    ],
  },
  ulybkapro: {
    title: 'Запись и личный кабинет',
    description: 'Запишитесь на приём или откройте личный кабинет: записи, приёмы и предложения.',
    buttonLabel: 'Записаться онлайн',
    url: 'https://app.ulybka.pro',
    extraActions: [
      {
        label: 'Личный кабинет',
        url: 'https://lk.ulybkamsk.ru/',
      },
    ],
  },
  'implant-beauty': {
    title: 'Запись и личный кабинет',
    description: 'Запишитесь на приём или откройте личный кабинет: записи, приёмы и предложения.',
    buttonLabel: 'Записаться онлайн',
    url: 'https://app.implant-beauty.ru',
    extraActions: [
      {
        label: 'Личный кабинет',
        url: 'https://lk.implant-beauty.ru',
      },
    ],
  },
};

export const okdocFeedbackResultCopy: Record<string, OkdocFeedbackResultCopy> = {
  positive: {
    title: 'Спасибо за высокую оценку',
    description: 'Нам очень приятно, что Вам понравился визит. Если захотите, можно оставить отзыв на внешней площадке по кнопке ниже.',
  },
  neutral: {
    title: 'Спасибо за обратную связь',
    description: 'Ваш ответ сохранён. Такие отметки помогают нам лучше понимать впечатление пациента от визита.',
  },
  negative: {
    title: 'Спасибо, мы уже приняли отзыв',
    description: 'Обратная связь передана руководству клиники. Команда сервиса разберёт ситуацию внутри, чтобы не выводить негативный опыт на внешние площадки.',
  },
};
